import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import { ArrowLeft, ArrowRight, Menu } from "lucide-react";
import { MarketingHeader } from "@/components/MarketingHeader";
import { MarketingFooter } from "@/components/MarketingFooter";
import { DocsSidebarContent } from "@/components/DocsSidebar";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { getPrevNextArticles } from "@/data/docsArticles";
import { useState } from "react";

interface DocsArticleLayoutProps {
  slug: string;
  children: React.ReactNode;
}

export default function DocsArticleLayout({ slug, children }: DocsArticleLayoutProps) {
  const { t } = useTranslation(['translation', 'common']);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const lp = useLocalizedPath();
  const { prev, next } = getPrevNextArticles(slug);

  // Lang-aware so /cs/docs/foo's prev/next + hub link stay on /cs/docs/*.
  // Bare paths here caused Google to demote 50+ /<lang>/docs/* URLs as
  // "Duplicate, different canonical" (GSC May 2026 validation failure).
  const buildLink = (articleSlug: string) => lp(`/docs/${articleSlug}`);

  const docsHubLink = lp("/docs");

  const handleCTA = () => {
    if (user) {
      navigate("/cctv-design");
    } else {
      navigate("/demo");
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground dark:bg-graphite-900 dark:text-white">
      {/* Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[var(--color-brand-blue)]/10 rounded-full blur-2xl opacity-20"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-600/10 rounded-full blur-2xl opacity-20"></div>
      </div>

      <MarketingHeader />

      <div className="pt-16 relative z-10 flex min-h-screen">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:block w-64 flex-shrink-0 border-r border-gray-200 bg-gray-50 dark:border-white/10 dark:bg-graphite-800/30">
          <div className="sticky top-16 overflow-y-auto max-h-[calc(100vh-4rem)] py-8 px-4">
            <Link to={docsHubLink} className="flex items-center gap-2 text-sm text-[var(--color-brand-blue)] hover:text-[var(--color-brand-blue)] dark:hover:text-blue-300 mb-6 px-3 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              {t('common:docs_back_to_docs')}
            </Link>
            <DocsSidebarContent currentSlug={slug} />
          </div>
        </aside>

        {/* Mobile Sidebar Toggle */}
        <div className="lg:hidden fixed z-40" style={{ bottom: 'max(24px, env(safe-area-inset-bottom))', left: 'max(24px, env(safe-area-inset-left))' }}>
          <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
            <SheetTrigger asChild>
              <Button size="lg" className="rounded-full w-12 h-12 p-0 bg-[var(--color-brand-blue)] hover:bg-[var(--color-brand-blue-hover)] shadow-sm">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="bg-background border-gray-200 dark:bg-graphite-900 dark:border-white/10 w-72 overflow-y-auto">
              <SheetTitle className="text-foreground dark:text-white text-lg font-semibold mb-4">{t('common:docs_hero_title')}</SheetTitle>
              <Link to={docsHubLink} className="flex items-center gap-2 text-sm text-[var(--color-brand-blue)] hover:text-[var(--color-brand-blue)] dark:hover:text-blue-300 mb-6 px-3 transition-colors" onClick={() => setSidebarOpen(false)}>
                <ArrowLeft className="w-4 h-4" />
                {t('common:docs_back_to_docs')}
              </Link>
              <div onClick={() => setSidebarOpen(false)}>
                <DocsSidebarContent currentSlug={slug} />
              </div>
            </SheetContent>
          </Sheet>
        </div>

        {/* Main Content */}
        <main className="flex-1 min-w-0">
          <div className="max-w-3xl mx-auto px-6 py-12">
            {children}

            {/* Prev / Next Navigation */}
            <div className="mt-16 pt-8 border-t border-gray-200 dark:border-white/10 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {prev ? (
                <Link to={buildLink(prev.slug)} className="group p-4 rounded-lg border border-gray-200 hover:border-[var(--color-brand-blue)] dark:border-white/10 dark:hover:border-[var(--color-brand-blue)] transition-all">
                  <span className="text-xs text-gray-500 uppercase tracking-wider">{t('common:previous')}</span>
                  <div className="flex items-center gap-2 mt-1">
                    <ArrowLeft className="w-4 h-4 text-[var(--color-brand-blue)] group-hover:-translate-x-1 transition-transform" />
                    <span className="text-sm text-foreground dark:text-white group-hover:text-[var(--color-brand-blue)] dark:group-hover:text-[var(--color-brand-blue)] transition-colors">{t(`common:${prev.titleKey}`)}</span>
                  </div>
                </Link>
              ) : <div />}
              {next ? (
                <Link to={buildLink(next.slug)} className="group p-4 rounded-lg border border-gray-200 hover:border-[var(--color-brand-blue)] dark:border-white/10 dark:hover:border-[var(--color-brand-blue)] transition-all text-right">
                  <span className="text-xs text-gray-500 uppercase tracking-wider">{t('common:next')}</span>
                  <div className="flex items-center justify-end gap-2 mt-1">
                    <span className="text-sm text-foreground dark:text-white group-hover:text-[var(--color-brand-blue)] dark:group-hover:text-[var(--color-brand-blue)] transition-colors">{t(`common:${next.titleKey}`)}</span>
                    <ArrowRight className="w-4 h-4 text-[var(--color-brand-blue)] group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              ) : <div />}
            </div>

            {/* CTA */}
            <div className="mt-16 p-8 rounded-none border border-neutral-200 dark:border-brand-border-strong bg-blue-50 dark:bg-graphite-800/50 relative overflow-hidden">
              <div className="absolute inset-0 opacity-20">
                <div className="absolute top-0 right-0 w-64 h-64 bg-[var(--color-brand-blue)]/10 rounded-full blur-3xl"></div>
              </div>
              <div className="relative z-10 text-center">
                <h3 className="text-2xl font-bold mb-3">
                  <span className="text-[var(--color-brand-blue)] dark:text-blue-400">
                    {t('common:docs_cta_title')}
                  </span>
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-lg mx-auto">{t('common:docs_cta_description')}</p>
                <Button size="lg" className="bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-white shadow-sm group" onClick={handleCTA}>
                  {t('common:docs_cta_button')}
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </div>
          </div>
        </main>
      </div>

      <MarketingFooter />
    </div>
  );
}
